import React from 'react';
import { motion } from 'framer-motion';
import { SiInstagram } from 'react-icons/si';
import { Instagram as InstagramIcon, ExternalLink } from 'lucide-react';
import { RESTAURANT_CONSTANTS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';

const instagramPosts = [
  {
    id: 1,
    image: IMAGES.AFRICAN_CUISINE_5,
    alt: "Plat africain traditionnel fait maison",
  },
  {
    id: 2,
    image: IMAGES.AFRICAN_CUISINE_6,
    alt: "Spécialité culinaire africaine authentique",
  },
  {
    id: 3,
    image: IMAGES.EUROPEAN_CUISINE_3,
    alt: "Raffinement de la cuisine européenne",
  },
  {
    id: 4,
    image: IMAGES.AFRICAN_CUISINE_7,
    alt: "Couleurs et saveurs de l'Afrique",
  },
  {
    id: 5,
    image: IMAGES.CAPTURE_D_CRAN6160,
    alt: "Détail d'un plat signature Blessing",
  },
  {
    id: 6,
    image: IMAGES.CAPTURE_D_CRAN6108,
    alt: "Produits frais et préparation artisanale",
  },
  {
    id: 7,
    image: IMAGES.CAPTURE_D_CRAN2854,
    alt: "Création culinaire africaine authentique",
  },
  {
    id: 8,
    image: IMAGES.CAPTURE_D_CRAN6495,
    alt: "Plat signature préparé avec passion",
  },
  {
    id: 9,
    image: IMAGES.CAPTURE_D_CRAN8415,
    alt: "Mélange harmonieux de saveurs",
  },
  {
    id: 10,
    image: IMAGES.CAPTURE_D_CRAN2274,
    alt: "Art culinaire Service-Pépinière Blessing",
  },
];

const springPresets = {
  gentle: {
    type: "spring",
    stiffness: 300,
    damping: 35,
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springPresets.gentle,
  },
};

export default function Instagram() {
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Hero Header Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-primary/5 -z-10" />
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={springPresets.gentle}
            className="inline-flex items-center justify-center p-4 rounded-full bg-accent/10 text-accent mb-6"
          >
            <InstagramIcon size={40} />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold text-primary mb-6"
          >
            Suivez nos délices
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10"
          >
            Plongez dans l'univers visuel de Service-Pépinière Blessing. 
            Découvrez nos nouveaux plats, les coulisses de notre cuisine et l'amour que nous mettons dans chaque assiette.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.3 }}
          >
            <Button
              asChild
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 py-6 rounded-full text-lg shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
            >
              <a 
                href={RESTAURANT_CONSTANTS.INSTAGRAM_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3"
              >
                <SiInstagram className="text-2xl" />
                Suivez-nous sur Instagram
                <ExternalLink size={18} className="opacity-70" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="container mx-auto px-4">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {instagramPosts.map((post) => (
            <motion.div
              key={post.id}
              variants={staggerItem}
              whileHover={{ scale: 1.02 }}
              className="group relative aspect-square rounded-2xl overflow-hidden shadow-md bg-muted border border-border"
            >
              <img
                src={post.image}
                alt={post.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-primary/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-md p-4 rounded-full text-white border border-white/30">
                  <SiInstagram size={28} />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 p-8 rounded-3xl bg-primary text-primary-foreground text-center"
        >
          <h3 className="text-2xl font-semibold mb-4">Partagez votre expérience</h3>
          <p className="text-primary-foreground/80 mb-6">
            N'oubliez pas de nous taguer sur vos photos de dégustation !
          </p>
          <p className="font-mono text-xl text-accent font-bold tracking-wider">
            @pepiniere_blessing
          </p>
        </motion.div>
      </section>

      {/* Floating Action Button (Simplified version for mobile reach) */}
      <div className="fixed bottom-8 right-8 z-50 md:hidden">
        <motion.a
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          href={RESTAURANT_CONSTANTS.INSTAGRAM_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] text-white shadow-2xl"
        >
          <SiInstagram size={24} />
        </motion.a>
      </div>
    </div>
  );
}
